import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/authContext';
import useFrontendUsage from '../../hooks/useFrontendUsage';
import { buildNewFrontendUrl, PUBLIC_PATHS } from '../../util/frontendMigration';
import { resolveViewTitle } from '../../util/viewTitle';

// Delay before the prompt opens, so it never slams into the user right at login.
const PROMPT_DELAY_MS = 8000;

// Recurring (backend-paced, currently every 14 days) prompt asking users of
// this legacy frontend why they still prefer it. Answers and dismissals feed
// the migration statistics; dismissing snoozes the prompt for one interval.
export default function LegacyFeedbackPrompt() {
  const auth = useAuth();
  const { getStatus, sendMigrationFeedback, trackSwitchToNewFrontend } = useFrontendUsage();

  const [open, setOpen] = useState(false);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [selectedReasons, setSelectedReasons] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const commentRef = useRef(null);
  const checkedThisSessionRef = useRef(false);

  useEffect(() => {
    if (!auth.token || !auth.userPreferencesHydrated || auth.isCounterDevice) return;
    if (checkedThisSessionRef.current) return;
    checkedThisSessionRef.current = true;

    let cancelled = false;
    let timer = null;
    const check = async () => {
      try {
        const status = await getStatus();
        if (cancelled || !status?.feedbackDue) return;
        setReasonOptions(Array.isArray(status.feedbackReasons) ? status.feedbackReasons : []);
        timer = setTimeout(() => {
          if (!PUBLIC_PATHS.has(window.location.pathname)) setOpen(true);
        }, PROMPT_DELAY_MS);
      } catch (_e) {
        // Status check is best effort — never bother the user about it.
      }
    };
    check();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per login session
  }, [auth.token, auth.userPreferencesHydrated, auth.isCounterDevice]);

  const currentContext = () => {
    const pathname = window.location.pathname;
    return {
      path: pathname,
      viewTitle:
        resolveViewTitle({ pathname, menuItems: auth.menuItems }) || undefined,
    };
  };

  const handleDismiss = () => {
    setOpen(false);
    sendMigrationFeedback({ dismissed: true, ...currentContext() }).catch(() => {});
  };

  const handleSubmit = async () => {
    const reasons = Object.keys(selectedReasons).filter(code => selectedReasons[code]);
    const comment = (commentRef.current?.value || '').trim();
    if (reasons.length === 0 && comment.length < 3) {
      toast.error('Bitte wähle mindestens einen Grund aus oder beschreibe kurz, warum.');
      return;
    }
    setSubmitting(true);
    try {
      await sendMigrationFeedback({ reasons, comment, ...currentContext() });
      toast.success('Danke für dein Feedback!');
      setOpen(false);
    } catch (_e) {
      // Toast comes from the hook; keep the dialog open so nothing is lost.
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenNewFrontend = async () => {
    const context = currentContext();
    setOpen(false);
    try {
      await sendMigrationFeedback({ dismissed: true, ...context });
    } catch (_e) {
      // Snoozing is best effort.
    }
    try {
      await trackSwitchToNewFrontend({ ...context, origin: 'feedback-dialog' });
    } catch (_e) {
      // Tracking must never block the switch.
    }
    window.location.assign(buildNewFrontendUrl(context.path));
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleDismiss} fullWidth maxWidth='sm'>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Kurze Frage: Was hält dich noch im bisherigen Management?
      </DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Du arbeitest gerade im bisherigen Tool. Dein Feedback entscheidet, was wir im neuen
          FunZone Management als Nächstes verbessern.
        </Typography>
        <FormGroup>
          {reasonOptions.map(reason => (
            <FormControlLabel
              key={reason.code}
              control={
                <Checkbox
                  checked={Boolean(selectedReasons[reason.code])}
                  onChange={event =>
                    setSelectedReasons(prev => ({
                      ...prev,
                      [reason.code]: event.target.checked,
                    }))
                  }
                />
              }
              label={reason.label}
            />
          ))}
        </FormGroup>
        <TextField
          inputRef={commentRef}
          label='Was genau? Je konkreter, desto besser (optional)'
          multiline
          minRows={3}
          maxRows={8}
          fullWidth
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          color='inherit'
          startIcon={<AutoAwesomeIcon />}
          onClick={handleOpenNewFrontend}
          sx={{ mr: 'auto' }}
        >
          Neues Tool jetzt öffnen
        </Button>
        <Button color='inherit' onClick={handleDismiss} disabled={submitting}>
          Später
        </Button>
        <Button variant='contained' onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Senden…' : 'Feedback senden'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
